"use client";

import * as React from "react";
import { motion } from "motion/react";

import { cn } from "../lib/cn";
import { springs, transitions } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Avatar } from "../primitives/Avatar";
import { Button } from "../primitives/Button";
import { Spinner } from "../primitives/Spinner";
import { Textarea } from "../primitives/Textarea";

/**
 * CommentPopover - a popover composer for adding a comment to an inline edit,
 * a clause, or any attribution chip. It folds the brand `<Popover>` into a
 * comment-specific shell:
 *
 *   - header: avatar + author name (caller-supplied, usually the current user)
 *   - body:   auto-grow textarea with placeholder ("Why this change?")
 *   - paste:  inline image-paste affordance - Cmd/Ctrl+V drops the clipboard
 *             image into the composer; consumers supply `onUploadImage` to
 *             persist the blob and return a public URL
 *   - footer: Cancel / Post controls (Cmd/Ctrl+Enter also posts)
 *
 * Composition: it reuses the lib's own `Popover` / `PopoverTrigger` /
 * `PopoverContent` (which portal, trap focus, dismiss on Escape + click
 * outside, and pass `side` / `align` / `sideOffset` through to Radix) plus the
 * brand `Avatar`, `Button`, `Spinner`, and `Textarea` primitives. It does not
 * import `@radix-ui/react-popover` directly. The panel springs in via
 * `motion/react`, gated on `useReducedMotion()` so reduced-motion users get a
 * static surface.
 *
 * The component is content-agnostic about *what* a comment is: it owns the UX,
 * not the persistence. `onSubmit` receives `{ text, imageUrl }`; the consumer
 * wires it to the right endpoint. It imports no app/office data model, route,
 * or service, and has no realtime / presence concern - those stay in the
 * consumer app, behind the injected seams.
 *
 * Image storage seam: `onUploadImage(blob, file?) => Promise<string>` - the
 * consumer uploads to its asset store and returns a URL. The component keeps
 * the URL in local state and ships it through to `onSubmit`. When
 * `onUploadImage` is omitted, image-paste and the file picker are silently
 * disabled.
 *
 * Pass `open` + `onOpenChange` to control the popover (e.g. opening when the
 * user clicks an attribution chip), or leave them out for uncontrolled use.
 */
export interface CommentPopoverSubmit {
  /** The trimmed comment body. */
  text: string;
  /** URL returned by `onUploadImage`, or null when no image was attached. */
  imageUrl?: string | null;
}

export interface CommentPopoverProps {
  /** Trigger element - usually an attribution chip or "Add comment" button. */
  children: React.ReactNode;
  /** Avatar URL of the comment author. Falls back to initials of `authorName`. */
  authorAvatarUrl?: string | null;
  /** Author display name. Used for the avatar fallback + a11y label. */
  authorName: string;
  /** Placeholder copy for the textarea. Default "Why this change?". */
  placeholder?: string;
  /** Seed the composer with existing text (e.g. editing a draft comment). */
  defaultValue?: string;
  /**
   * Persist a pasted / uploaded image. The blob is whatever the user pasted;
   * the consumer uploads it and returns a public URL. Image-paste and the
   * file picker are disabled when omitted.
   */
  onUploadImage?: (blob: Blob, file?: File) => Promise<string>;
  /** Fires when the user posts. Receives `{ text, imageUrl }`. May be async. */
  onSubmit: (payload: CommentPopoverSubmit) => void | Promise<void>;
  /** Fires when the user clicks Cancel or dismisses without posting. */
  onCancel?: () => void;
  /** Controlled open state. */
  open?: boolean;
  /** Controlled open-change handler. */
  onOpenChange?: (open: boolean) => void;
  /** Side of the trigger to place the popover on. Default "right". */
  side?: "top" | "right" | "bottom" | "left";
  /** Alignment along that side. Default "start". */
  align?: "start" | "center" | "end";
  /** Px gap between trigger and popover. Default 8. */
  sideOffset?: number;
  /** Submit button label. Default "Post". */
  submitLabel?: string;
  /** Cancel button label. Default "Cancel". */
  cancelLabel?: string;
  /** Class name for the popover content surface. */
  contentClassName?: string;
}

/**
 * Extract the first image from a paste event's clipboard items. Returns null
 * if the paste contained no image.
 */
export function imageFromPaste(
  event: React.ClipboardEvent,
): { blob: Blob; file?: File } | null {
  const items = event.clipboardData?.items;
  if (!items) return null;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind === "file" && item.type.startsWith("image/")) {
      const file = item.getAsFile();
      if (file) return { blob: file, file };
    }
  }
  return null;
}

export const CommentPopover: React.FC<CommentPopoverProps> = ({
  children,
  authorAvatarUrl,
  authorName,
  placeholder = "Why this change?",
  defaultValue = "",
  onUploadImage,
  onSubmit,
  onCancel,
  open,
  onOpenChange,
  side = "right",
  align = "start",
  sideOffset = 8,
  submitLabel = "Post",
  cancelLabel = "Cancel",
  contentClassName,
}) => {
  const reduced = useReducedMotion();
  const allowMotion = reduced !== true;

  const [text, setText] = React.useState(defaultValue);
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [posting, setPosting] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const headingId = React.useId();

  const reset = React.useCallback(() => {
    setText(defaultValue);
    setImageUrl(null);
    setUploading(false);
    setPosting(false);
  }, [defaultValue]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) reset();
      onOpenChange?.(next);
    },
    [onOpenChange, reset],
  );

  const upload = React.useCallback(
    async (blob: Blob, file?: File) => {
      if (!onUploadImage) return;
      setUploading(true);
      try {
        const url = await onUploadImage(blob, file);
        setImageUrl(url);
      } finally {
        setUploading(false);
      }
    },
    [onUploadImage],
  );

  const handlePaste = React.useCallback(
    (event: React.ClipboardEvent) => {
      if (!onUploadImage) return;
      const image = imageFromPaste(event);
      if (!image) return;
      event.preventDefault();
      void upload(image.blob, image.file);
    },
    [onUploadImage, upload],
  );

  const handleFileChosen = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;
      void upload(file, file);
    },
    [upload],
  );

  const canSubmit = (!!text.trim() || !!imageUrl) && !uploading && !posting;

  const submit = React.useCallback(async () => {
    if (!text.trim() && !imageUrl) return;
    setPosting(true);
    try {
      await onSubmit({ text: text.trim(), imageUrl });
      handleOpenChange(false);
    } finally {
      setPosting(false);
    }
  }, [text, imageUrl, onSubmit, handleOpenChange]);

  const handleSubmit = React.useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      void submit();
    },
    [submit],
  );

  // Cmd/Ctrl+Enter posts from the textarea - the keyboard path that matches
  // the click on Post, so keyboard users never have to leave the field.
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        void submit();
      }
    },
    [submit],
  );

  const handleCancel = React.useCallback(() => {
    onCancel?.();
    handleOpenChange(false);
  }, [onCancel, handleOpenChange]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={12}
        role="dialog"
        aria-labelledby={headingId}
        className={cn(
          "w-80 overflow-hidden p-0 motion-reduce:animate-none",
          contentClassName,
        )}
      >
        <motion.form
          onSubmit={handleSubmit}
          className="flex flex-col"
          initial={allowMotion ? { scale: 0.98, opacity: 0 } : false}
          animate={{ scale: 1, opacity: 1 }}
          transition={allowMotion ? springs.gentle : transitions.surface}
        >
          <header className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Avatar
              src={authorAvatarUrl ?? undefined}
              name={authorName}
              size="xs"
              colorFromName
            />
            <span id={headingId} className="text-sm font-medium text-foreground">
              {authorName}
            </span>
          </header>
          <Textarea
            autoFocus
            variant="ghost"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={3}
            aria-label="Comment"
            className="min-h-[72px] resize-none px-3 py-2 text-sm"
          />
          {imageUrl ? (
            <div className="relative px-3 pb-2">
              <img
                src={imageUrl}
                alt="Comment attachment"
                className="max-h-40 w-full rounded-md border border-border object-cover"
              />
              <button
                type="button"
                onClick={() => setImageUrl(null)}
                className="absolute right-4 top-1 rounded-full bg-card/90 px-2 py-0.5 text-xs text-muted-foreground hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label="Remove attachment"
              >
                Remove
              </button>
            </div>
          ) : null}
          {uploading ? (
            <div className="flex items-center gap-2 border-t border-border px-3 py-2 text-xs text-muted-foreground">
              <Spinner size="sm" decorative />
              <span>Uploading attachment.</span>
            </div>
          ) : null}
          <footer className="flex items-center justify-between border-t border-border bg-card/40 px-3 py-2">
            {onUploadImage ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChosen}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
                  disabled={uploading || posting}
                >
                  Attach image
                </button>
              </>
            ) : (
              <span className="text-xs text-muted-foreground">
                Cmd+V to paste a screenshot.
              </span>
            )}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                disabled={posting}
              >
                {cancelLabel}
              </Button>
              <Button type="submit" variant="default" size="sm" isLoading={posting} disabled={!canSubmit}>
                {submitLabel}
              </Button>
            </div>
          </footer>
        </motion.form>
      </PopoverContent>
    </Popover>
  );
};
CommentPopover.displayName = "CommentPopover";
